import { parseDsn } from './dsn.js';
import { authHeader, serializeEnvelope } from './envelope.js';
import { buildEvent } from './event.js';
export const REPORTER_CLIENT = 'steelwake-reporter/1';
const DEFAULT_MAX_PER_MINUTE = 30;
const DEFAULT_TIMEOUT_MS = 5000;
const DEFAULT_PAUSE_MS = 60_000;
function eventId() {
    return globalThis.crypto.randomUUID().replace(/-/g, '');
}
/**
 * Sends errors to any Sentry-protocol ingest (Sentry, GlitchTip) as envelopes over `fetch`
 * (ADR-0005). Small on purpose: no SDK, no instrumentation, nothing on the hot path but a
 * queued request. A failure to report is logged and forgotten; it never affects the caller.
 */
export function createErrorReporter(options) {
    const dsnString = options.dsn?.trim() ?? '';
    const now = options.now ?? (() => new Date());
    const fetchImpl = options.fetch ?? globalThis.fetch;
    const maxPerMinute = options.maxPerMinute ?? DEFAULT_MAX_PER_MINUTE;
    const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
    let parsed = null;
    if (dsnString.length > 0) {
        try {
            parsed = parseDsn(dsnString);
        }
        catch (err) {
            options.onFailure?.(`error reporting disabled: ${err instanceof Error ? err.message : String(err)}`);
        }
    }
    const dsn = parsed;
    const pending = new Set();
    const sentAt = [];
    let pausedUntil = 0;
    let dropped = 0;
    function allowed(atMs) {
        if (atMs < pausedUntil)
            return false;
        while (sentAt.length > 0 && (sentAt[0] ?? 0) <= atMs - 60_000)
            sentAt.shift();
        if (sentAt.length >= maxPerMinute)
            return false;
        sentAt.push(atMs);
        return true;
    }
    function deliver(body, at) {
        if (!dsn)
            return Promise.resolve();
        return fetchImpl(dsn.envelopeUrl, {
            method: 'POST',
            headers: {
                'content-type': 'application/x-sentry-envelope',
                'x-sentry-auth': authHeader(dsn.publicKey, REPORTER_CLIENT),
            },
            body,
            signal: AbortSignal.timeout(timeoutMs),
        })
            .then((res) => {
            if (res.status === 429) {
                const retry = Number(res.headers.get('retry-after') ?? '');
                pausedUntil =
                    at.getTime() + (Number.isFinite(retry) && retry > 0 ? retry * 1000 : DEFAULT_PAUSE_MS);
                options.onFailure?.(`error reporting paused by the ingest (429)`);
            }
            else if (!res.ok) {
                options.onFailure?.(`error report refused: ${String(res.status)}`);
            }
        })
            .catch((err) => {
            options.onFailure?.(`error report failed: ${err instanceof Error ? err.message : String(err)}`);
        });
    }
    return {
        get enabled() {
            return dsn !== null;
        },
        get dropped() {
            return dropped;
        },
        capture(thrown, captureOptions = {}) {
            if (!dsn)
                return null;
            const at = now();
            if (!allowed(at.getTime())) {
                dropped++;
                return null;
            }
            try {
                const event = buildEvent(thrown, {
                    app: options.app,
                    platform: options.platform,
                    eventId: eventId(),
                    timestamp: at,
                    release: options.release,
                    environment: options.environment,
                    serverName: options.serverName,
                    level: captureOptions.level,
                    handled: captureOptions.handled,
                    tags: captureOptions.tags,
                    extra: captureOptions.extra,
                });
                const body = serializeEnvelope(event, dsnString, at);
                const delivery = deliver(body, at).finally(() => pending.delete(delivery));
                pending.add(delivery);
                return event.event_id;
            }
            catch (err) {
                options.onFailure?.(`error report not built: ${err instanceof Error ? err.message : String(err)}`);
                return null;
            }
        },
        async flush() {
            await Promise.allSettled([...pending]);
        },
    };
}
//# sourceMappingURL=reporter.js.map